/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Event tracking spec", description = "An overview of the tracking API", lastUpdatedDate = new Date(1695254400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Event tracking spec",
        description: "An overview of the tracking API",
        lastUpdatedDate: "Thu Sep 21 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "depth": 1,
          "id": "identify-events"
        }, {
          "depth": 1,
          "id": "track-events"
        }, {
          "depth": 1,
          "id": "page-events"
        }, {
          "depth": 1,
          "id": "screen-events"
        }, {
          "depth": 1,
          "id": "group-events"
        }, {
          "value": "Automatically captured fields",
          "depth": 1,
          "id": "automatically-captured-fields"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h3: "h3",
      strong: "strong",
      em: "em",
      ul: "ul",
      li: "li",
      pre: "pre",
      code: "code",
      span: "span",
      a: "a",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Hightouch Events provides a flexible interface for tracking both user traits\nand user events. This flexibility allows you to adapt the tracking to your\nspecific business context. With the tracking API, you can power a wide range of\nuse cases, from user activation to detailed analytics."
      }), "\n", _jsx(_components.h3, {
        id: "identify-events",
        children: _jsx(_components.strong, {
          children: "Identify events"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Identify events"
        }), " capture data about ", _jsx(_components.em, {
          children: "who"
        }), " your users are. This user context\nis vital for understanding and targeting your users. For example, you might use\nthe collected identifiers in followup email campaigns, to segment your users by\nlocation, or to link anonymous and authenticated events."]
      }), "\n", _jsx(_components.p, {
        children: "Identify events have three main components:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["An ", _jsx(_components.strong, {
            children: "anonymous id"
          }), ". Anonymous ids are used to tie together events coming\nfrom the ", _jsx(_components.strong, {
            children: "same device"
          }), ". Tying additional user ids or traits to the an\nanonymous id lets you better understand other events coming from the same\ndevice. The anonymous id is autogenerated and stored on the client, and is\nincluded in other event types such as Track events."]
        }), "\n", _jsxs(_components.li, {
          children: ["A ", _jsx(_components.strong, {
            children: "user id"
          }), ". A user ID is a persistent identifier that's associated with\nthe same user across devices and sessions. For example, it's common to use a\nuser ID generated by your database. You should avoid using identifiers that\nmay change, such as email, since if the user updates their email you will\nstill want to treat them as the user."]
        }), "\n", _jsxs(_components.li, {
          children: ["Additional ", _jsx(_components.strong, {
            children: "user traits"
          }), ". You may optionally provide additional user\ntraits. Examples include the user's email, name, or age."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Example Payload for Identify Event:"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"identify\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"messageId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"111e984d-c93c-444c-b29c-f499a117c500\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"traits\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"email\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"kevin@hightouch.io\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"anonymousId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ce576487-95f9-4262-9315-2c7942b061c8\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"userId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"originalTimestamp\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:05:47.063Z\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sentAt\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:05:47.064Z\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "track-events",
        children: _jsx(_components.strong, {
          children: "Track events"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Track events"
        }), " capture data on ", _jsx(_components.em, {
          children: "what"
        }), " the user is doing. Track events can\ncapture a broad range of actions, such as clicking a button, completing a\npurchase, or creating a new account."]
      }), "\n", _jsxs(_components.p, {
        children: ["Additional information about the event can be included in the ", _jsx(_components.strong, {
          children: _jsx(_components.code, {
            children: "properties"
          })
        }), "\nfield. For example, for a ", _jsx(_components.code, {
          children: "Purchase Complete"
        }), " event, you may want to include\nthe ", _jsx(_components.code, {
          children: "product ids"
        }), " of the purchased products."]
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing into the warehouse, the ", _jsx(_components.code, {
          children: "event"
        }), " field is used to generate ", _jsx(_components.a, {
          href: "./warehouse-schema#per-event-tables",
          children: "table\nnames"
        }), " in the warehouse. Additionally, the\n", _jsx(_components.code, {
          children: "properties"
        }), " field is autoconverted into\n", _jsx(_components.a, {
          href: "./warehouse-schema#per-event-tables",
          children: "columns"
        }), " in the warehouse."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Example Payload for Track Event:"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"track\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"messageId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"7b1e2872-4ebb-4779-b9ad-e7aa1a8eab82\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"anonymousId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ce576487-95f9-4262-9315-2c7942b061c8\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"userId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"event\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Purchase completed\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"cart_total\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "24.99"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"originalTimestamp\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:12:00.584Z\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sentAt\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:12:00.585Z\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "page-events",
        children: _jsx(_components.strong, {
          children: "Page events"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Page events"
        }), " track the webpages users interact with. In most situations, you\nshould automatically call the page load event when a page is loaded."]
      }), "\n", _jsx(_components.p, {
        children: "For single page apps, you should simulate page events in your routing\nlayer."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Example Payload for Page Event:"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"page\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"messageId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"bb9fcbbd-9e19-4bfd-a726-7b1e7e44f8ba\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"anonymousId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ce576487-95f9-4262-9315-2c7942b061c8\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"userId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"title\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Home\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"originalTimestamp\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:05:47.061Z\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sentAt\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:05:47.063Z\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "screen-events",
        children: _jsx(_components.strong, {
          children: "Screen events"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Screen events"
        }), " are the equivalent of page views for ", _jsx(_components.em, {
          children: "mobile apps"
        }), ". Like\npage events, you should automatically call the screen event when a mobile\nscreen view is loaded."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Example Payload for Screen Event:"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"screen\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"messageId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"bb9fcbbd-9e19-4bfd-a726-7b1e7e44f8ba\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"anonymousId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ce576487-95f9-4262-9315-2c7942b061c8\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"userId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"title\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Home\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"originalTimestamp\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:05:47.061Z\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sentAt\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:05:47.063Z\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "group-events",
        children: _jsx(_components.strong, {
          children: "Group events"
        })
      }), "\n", _jsx(_components.p, {
        children: "Group events let you associate users with a larger group. For example, a user\nmay be part of an organization, or family."
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "traits"
        }), " field is optional, and can include information on traits on the\ngroup. For example, if linking a user to a company, you may include traits on\nthe company such as the company size or location."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Example Payload for Group Event:"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"group\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"messageId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"d87596da-5f1e-40f4-a28a-7099290548b7\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"anonymousId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ce576487-95f9-4262-9315-2c7942b061c8\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"userId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"groupId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"456\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"traits\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"company_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Hightouch\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"originalTimestamp\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:46:23.098Z\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sentAt\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2023-09-22T18:46:23.099Z\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "automatically-captured-fields",
        children: "Automatically captured fields"
      }), "\n", _jsx(_components.p, {
        children: "Below is a chart that shows you which context variables are populated automatically by the iOS, Android, and Javascript libraries."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "CONTEXT FIELD"
            }), _jsx(_components.th, {
              children: "Javascript"
            }), _jsx(_components.th, {
              children: "iOS"
            }), _jsx(_components.th, {
              children: "Android"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "app.name"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Name of the application."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "app.version"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Version of the application."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "app.build"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Build of the application."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "campaign.name"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "Name of the campaign."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "campaign.source"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "Source of the campaign."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "campaign.medium"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "Medium of the campaign."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "campaign.term"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "Term of the campaign."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "campaign.content"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "Content of the campaign."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "device.type"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Type of the device."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "device.id"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "ID of the device."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "device.advertisingId"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Advertising ID of the device."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "device.adTrackingEnabled"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "If ad tracking is enabled on the device."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "device.manufacturer"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Manufacturer of the device."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "device.model"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Model of the device."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "device.name"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Name of the device."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "library.name"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Name of the library."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "library.version"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Version of the library."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ip"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Current user’s IP address."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "locale"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Locale string of the user."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "network.bluetooth"
            }), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Bluetooth information."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "network.carrier"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Carrier information about the network connection."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "network.cellular"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Cellular information about the network connection."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "network.wifi"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "WiFi information about the network connection."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "os.name"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Name of the operating system."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "os.version"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Version of the operating system."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "page.path"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "Path of the current page in the browser."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "page.referrer"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "Referrer of the current page in the browser."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "page.search"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "Search of the current page in the browser."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "page.title"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "Title of the current page in the browser."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "page.url"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "URL of the current page in the browser."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "screen.density"
            }), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Density of the device’s screen."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "screen.height"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Height of the device’s screen."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "screen.width"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Width of the device’s screen."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "traits"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Traits of the user."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "userAgent"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "User agent of the device making the request."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "userAgentData"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "Information about the user’s timezone."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "timezone"
            }), _jsx(_components.td, {}), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "Information about the user’s timezone."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "IP Addresses are captured by Hightouch's servers when a message is received for client-side events and is not captured by Hightouch's libraries."
        }), "\n", _jsx(_components.li, {
          children: "userAgentData is only collected if the Client Hints API is available on the browser."
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
